import React, { useRef } from "react";
import moment from "moment";
import { func } from "prop-types";
import { Formik, Form, Field } from "formik";
import { Row, Col, Collapse } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

import { ButtonWithSpinner } from "../../../../../components/common";
import {
  scoringResultsIsLoadingSelector,
  scoringResultsReportFiltersSelector,
  scoringResultsReportPageInfoSelector,
} from "../../../selectors";
import { LabeledDatePicker, Select } from "../../../../../components/formik";
import {
  INITIAL_FILTERS_SCORING_RESULT_CONSTANTS,
  SCORING_RESULTS_FILTERS_LABELS,
  scoringResultTypes,
} from "../../../constants/scoringResultsReportConstants";
import { CollapseButton, CollapseOpenIcon } from "../../../../../assets";
import Clickable from "../../../../../components/common/Clickable";
import "./scoringResultReportForm.css";
import {
  getScoringResultsReportEntries,
  getScoringResultsReportEntriesCount,
  setSearchFilters,
} from "../../../actions";
import { FaTimes } from "react-icons/fa";
import {
  DATE_WITH_CURRENT_YEAR,
  DATE_WITH_MIN_YEAR,
} from "../../../../../utilities/common";
import { getFiltersValues } from "../../../../../utilities/filters";

const ScoringResultsReportForm = ({
  handleSubmit,
  isOpenCollapse,
  toggleCollapse,
  entries,
}) => {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const isLoading = useSelector(scoringResultsIsLoadingSelector);
  const filters = useSelector(scoringResultsReportFiltersSelector);
  const { itemsPerPage } = useSelector(scoringResultsReportPageInfoSelector);

  return (
    <Formik ref={formikRef} onSubmit={handleSubmit}>
      {({ errors, values }) => {
        const { dateFrom: dateFromValue, dateTo: dateToValue } = values;

        return (
          <div>
            <div className="search-scoring-reports-search">
              <Collapse isOpen={isOpenCollapse}>
                <Form className="search-scoring-reports-form">
                  <Row className="search-scoring-reports-inner-container">
                    <Col lg={3}>
                      <LabeledDatePicker
                        dateFormat="dd.MM.yyyy"
                        name="dateFrom"
                        placeholderText="Date From"
                        minDate={DATE_WITH_MIN_YEAR}
                        maxDate={
                          dateToValue
                            ? new Date(dateToValue)
                            : DATE_WITH_CURRENT_YEAR
                        }
                        openToDate={new Date(moment())}
                      />
                    </Col>
                    <Col lg={3}>
                      <LabeledDatePicker
                        type="text"
                        dateFormat="dd.MM.yyyy"
                        name="dateTo"
                        placeholderText="Date To"
                        maxDate={DATE_WITH_CURRENT_YEAR}
                        minDate={
                          dateFromValue
                            ? new Date(dateFromValue).getTime()
                            : DATE_WITH_MIN_YEAR
                        }
                        openToDate={new Date(moment())}
                      />
                    </Col>
                    <Col lg={6}>
                      <Field
                        type="text"
                        name="scoringType"
                        component={Select}
                        className="dropdown-group"
                        placeholder="Scoring Type"
                        options={scoringResultTypes}
                        isClearable
                      />
                    </Col>
                    <Col lg={12}>
                      {errors.atLeastOneOf && (
                        <React.Fragment>
                          <div className="form-error-message">
                            {errors.atLeastOneOf}
                          </div>
                        </React.Fragment>
                      )}
                    </Col>
                    <Col lg={12} className="d-flex justify-content-center p-3">
                      <ButtonWithSpinner
                        id="searchScoringResultsEntriesSubmitButton"
                        text="Search"
                        outline
                        type="submit"
                        className="search-button-scoring-report"
                        loading={isLoading}
                      />
                    </Col>
                  </Row>
                </Form>
                <div className="filters-menu-collapse-button-scoring-results">
                  {entries.length ? (
                    <Clickable onClick={toggleCollapse}>
                      <CollapseButton />
                    </Clickable>
                  ) : null}
                </div>
              </Collapse>
            </div>
            <div className="filters-menu-collapse-button-scoring-results">
              <Clickable onClick={toggleCollapse}>
                {!isOpenCollapse ? <CollapseOpenIcon /> : null}
              </Clickable>
            </div>
            <div className="search-scoring-reports-filters-main-container">
              {Object.values(filters).some(Boolean) ? (
                <div className="filters-menu-label">Filter Applied</div>
              ) : null}

              {getFiltersValues(filters).map(([key, value]) => (
                <SingleFilter
                  filterKey={key}
                  filterValue={value}
                  clearFilter={() => clearSingleFilter(key)}
                />
              ))}
              {Object.values(filters).some(
                (x) => x !== undefined && x !== null && x !== ""
              ) ? (
                <div
                  onClick={clearFilters}
                  className="filters-menu-label-clear"
                >
                  Clear All
                </div>
              ) : null}
            </div>
          </div>
        );
      }}
    </Formik>
  );

  function clearFilters() {
    dispatch(setSearchFilters(INITIAL_FILTERS_SCORING_RESULT_CONSTANTS));
    formikRef.current.setValues(INITIAL_FILTERS_SCORING_RESULT_CONSTANTS);
    toggleCollapse();
  }

  function clearSingleFilter(filterKey) {
    let val = "";
    const currentFilters = { ...filters };

    dispatch(
      dispatch(
        setSearchFilters({
          [filterKey]: val,
        })
      )
    );

    currentFilters[filterKey] = val;
    formikRef.current.setFieldValue(filterKey, val);
    if (Object.values(currentFilters).filter(Boolean).length) {
      dispatch(getScoringResultsReportEntriesCount(currentFilters));
      dispatch(getScoringResultsReportEntries({ take: itemsPerPage }));
    } else {
      toggleCollapse();
    }
  }

  function SingleFilter({
    filterKey = "",
    filterValue = "",
    clearFilter = () => {},
  }) {
    let currentValue = filterValue;
    if (filterKey === "scoringType") {
      // set type label
      currentValue = scoringResultTypes.find(
        (type) => type.value === filterValue
      ).label;
    }

    if ((filterKey === "dateFrom" || filterKey === "dateTo") && filterValue) {
      // format date
      currentValue = moment(new Date(filterValue)).format("DD.MM.yyyy");
    }

    return (
      <div className="single-filter-box">
        <div className="single-filter-label">
          {SCORING_RESULTS_FILTERS_LABELS[filterKey]}
        </div>
        <div onClick={toggleCollapse} className="single-filter-chip">
          {currentValue}
          <FaTimes
            className="remove-filter-icon"
            onClick={(e) => {
              e.stopPropagation();
              clearFilter();
            }}
            color="#707070"
          />
        </div>
      </div>
    );
  }
};

ScoringResultsReportForm.propTypes = {
  handleSubmit: func.isRequired,
};

export default ScoringResultsReportForm;



// WEBPACK FOOTER //
// ./src/features/reports/pages/scoring-results-report-page/scoring-results-form/ScoringResultsReportForm.jsx